/* eslint-disable max-lines */
import { ArrowPathIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { FC, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { IAdminPassenger } from "../../../interfaces/reservations/Reservations.types";
import {
  adminEditPassengerNames,
  getAdminPassengers
} from "../../../services/Reservations.service";
import Button from "../../ui/Button";
import Modal from "../../ui/Modal";

interface IPassengerNamesModalProps {
  show: boolean;
  onClose: () => void;
  uniqueJourneyId: string;
  onSuccess?: () => void;
}

const MIN_NAME_LENGTH = 2;
const MAX_NAME_LENGTH = 50;

// Mirrors the server-side guard in PassengerAdminService: letters of any script plus combining
// marks, with spaces, apostrophes, dashes and periods allowed only internally. Deliberately
// wider than the storefront rule, which rejects hyphens and periods.
const NAME_REGEX = /^\p{L}[\p{L}\p{M}\p{Pd}'’.\s]*\p{L}$/u;

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg " +
  "focus:outline-none focus:ring-2 focus:ring-primary/40";

const NO_ACCESS_MESSAGE =
  "Nuk keni leje për të parë apo modifikuar pasagjerët e këtij rezervimi.";

const statusOf = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status;

// A refusal from [SuperAdminRoleFilter] serialises ModelState, not { message }, so 401/403 is
// mapped explicitly instead of falling through to the generic failure text.
const errMessage = (error: unknown, fallback: string) => {
  const status = statusOf(error);
  if (status === 401 || status === 403) return NO_ACCESS_MESSAGE;

  return (
    (error as { response?: { data?: { message?: string } } })?.response?.data
      ?.message || fallback
  );
};

const isNameValid = (value: string) => {
  const trimmed = value.trim();
  return (
    trimmed.length >= MIN_NAME_LENGTH &&
    trimmed.length <= MAX_NAME_LENGTH &&
    NAME_REGEX.test(trimmed) &&
    !trimmed.includes("  ")
  );
};

const PassengerNamesModal: FC<IPassengerNamesModalProps> = ({
  show,
  onClose,
  uniqueJourneyId,
  onSuccess
}) => {
  const [passengers, setPassengers] = useState<IAdminPassenger[] | null>(null);
  // Working copy, only populated in edit mode so cancelling restores the saved names.
  const [draft, setDraft] = useState<IAdminPassenger[]>([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [saving, setSaving] = useState(false);
  const [savedOnce, setSavedOnce] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const loadPassengers = async () => {
    setLoading(true);
    try {
      const res = await getAdminPassengers(uniqueJourneyId);
      setPassengers(res.data.passengers);
    } catch (error) {
      const status = statusOf(error);
      if (status === 401 || status === 403) setForbidden(true);
      toast.error(
        errMessage(error, "Ndodhi një gabim gjatë marrjes së pasagjerëve")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      setMode("view");
      setSavedOnce(false);
      setForbidden(false);
      void loadPassengers();
    }
  }, [show, uniqueJourneyId]);

  const startEdit = () => {
    if (!passengers) return;
    setDraft(passengers.map((p) => ({ ...p })));
    setMode("edit");
  };

  const updateDraft = (
    id: number,
    field: "firstName" | "lastName",
    value: string
  ) =>
    setDraft((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );

  const saveInvalid = draft.some(
    (p) => !isNameValid(p.firstName) || !isNameValid(p.lastName)
  );

  // Only send what actually changed — the endpoint is a no-op otherwise, but this keeps the
  // audit row and the reported row count meaningful.
  const changed = draft.filter((p) => {
    const original = passengers?.find((x) => x.id === p.id);
    return (
      !!original &&
      (original.firstName !== p.firstName.trim() ||
        original.lastName !== p.lastName.trim())
    );
  });

  const handleSave = async () => {
    if (saveInvalid) return;
    if (changed.length === 0) {
      setMode("view");
      return;
    }
    setSaving(true);
    try {
      const res = await adminEditPassengerNames(
        uniqueJourneyId,
        changed.map((p) => ({
          id: p.id,
          firstName: p.firstName.trim(),
          lastName: p.lastName.trim()
        }))
      );
      setPassengers(res.data.passengers);
      setMode("view");
      setSavedOnce(true);
      toast.success("Emrat e pasagjerëve u përditësuan me sukses");
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(
        errMessage(error, "Ndodhi një gabim gjatë përditësimit të pasagjerëve")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onClose={onClose}
      position="center"
      title={
        <span className="flex items-center gap-2 font-medium">
          <UserGroupIcon className="w-5 h-5 text-primary" />
          Pasagjerët
        </span>
      }
      modalWidth="440px"
      calcHeight={false}
      // Hug the content: the view list is short, the edit form is taller, and neither should
      // render a full-height box. Scrolls only once the passenger list outgrows the cap.
      fitContent
    >
      <div className="p-4">
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <ArrowPathIcon className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : forbidden ? (
          <p className="text-center text-gray-500 py-8 text-sm">
            {NO_ACCESS_MESSAGE}
          </p>
        ) : !passengers || passengers.length === 0 ? (
          <p className="text-center text-gray-500 py-8 text-sm">
            Të dhënat e pasagjerëve nuk u gjetën.
          </p>
        ) : mode === "view" ? (
          <>
            {passengers.map((passenger, index) => (
              <div
                key={passenger.id}
                className="flex items-center gap-2 py-1.5 border-b border-gray-100 last:border-0"
              >
                <span className="text-sm text-gray-500 w-5 shrink-0">
                  {index + 1}.
                </span>
                <span className="text-sm font-medium">
                  {passenger.firstName} {passenger.lastName}
                </span>
              </div>
            ))}

            {savedOnce && (
              <p className="text-xs text-gray-500 mt-3">
                Bileta PDF po rigjenerohet me emrat e rinj. Nëse klienti duhet
                njoftuar, përdorni butonin e email-it te rezervimi.
              </p>
            )}

            <div className="flex gap-2 justify-end mt-5">
              <Button type="button" onClick={startEdit}>
                Modifiko
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-4">
              {draft.map((passenger, index) => (
                <div key={passenger.id}>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    {index + 1}. Pasagjeri
                  </p>
                  <div className="flex gap-2">
                    <div className="w-1/2">
                      <label className="block text-xs text-gray-500 mb-1">
                        Emri
                      </label>
                      <input
                        type="text"
                        maxLength={MAX_NAME_LENGTH}
                        value={passenger.firstName}
                        onChange={(e) =>
                          updateDraft(passenger.id, "firstName", e.target.value)
                        }
                        className={`${inputClass} ${
                          isNameValid(passenger.firstName)
                            ? ""
                            : "border-red-400 focus:ring-red-200"
                        }`}
                      />
                    </div>
                    <div className="w-1/2">
                      <label className="block text-xs text-gray-500 mb-1">
                        Mbiemri
                      </label>
                      <input
                        type="text"
                        maxLength={MAX_NAME_LENGTH}
                        value={passenger.lastName}
                        onChange={(e) =>
                          updateDraft(passenger.id, "lastName", e.target.value)
                        }
                        className={`${inputClass} ${
                          isNameValid(passenger.lastName)
                            ? ""
                            : "border-red-400 focus:ring-red-200"
                        }`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {saveInvalid && (
              <p className="text-xs text-red-600 mt-3">
                Emri dhe mbiemri duhet të kenë {MIN_NAME_LENGTH}–
                {MAX_NAME_LENGTH} karaktere dhe të përmbajnë vetëm shkronja,
                hapësira, apostrofa, viza ose pika.
              </p>
            )}

            <div className="flex gap-2 justify-end mt-5">
              <Button
                type="button"
                btnType="custom"
                onClick={() => setMode("view")}
                disabled={saving}
                className="bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Kthehu
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={saving || saveInvalid}
                className="flex items-center"
              >
                {saving && (
                  <ArrowPathIcon className="w-4 h-4 animate-spin mr-2" />
                )}
                Ruaj
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default PassengerNamesModal;
